import { useEffect, useState } from 'react';
import axios from 'axios';
import { BookOpenText, CheckCircle, Warning, ArrowRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHECKABLE_EXTS = new Set(['txt', 'pdf', 'docx', 'pptx']);

function extOf(name) {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

/**
 * Post-download grammar-check card. Runs a check on the converted file
 * (text-bearing outputs only) and shows the number of errors detected,
 * plus a shortcut to the grammar-corrector tool.
 */
export default function GrammarCheckPanel({ result }) {
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [data, setData] = useState(null);

  const ext = extOf(result.filename);
  const eligible = CHECKABLE_EXTS.has(ext);

  useEffect(() => {
    if (!eligible) return;
    let cancelled = false;
    setState('loading');
    (async () => {
      try {
        const r = await axios.get(`${API}/grammar/${result.job_id}`, { timeout: 60000 });
        if (cancelled) return;
        setData(r.data);
        setState('done');
      } catch (e) {
        if (cancelled) return;
        setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [result.job_id, eligible]);

  if (!eligible) return null;

  const count = data?.error_count ?? 0;
  const clean = state === 'done' && count === 0;
  const showIssues = state === 'done' && count > 0;

  return (
    <div
      data-testid="grammar-panel"
      className="mt-6 border-2 border-black bg-white p-5 brutal-shadow text-left"
    >
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 grid place-items-center border-2 border-black bg-yellow-300">
          <BookOpenText weight="bold" size={20} />
        </span>
        <div className="min-w-0">
          <p className="font-display font-semibold text-lg">Grammar check</p>
          <p className="text-xs font-mono-plex text-zinc-500 uppercase tracking-widest">
            Automatic scan of your converted document
          </p>
        </div>
      </div>

      <div className="mt-4">
        {state === 'loading' && (
          <p data-testid="grammar-loading" className="text-sm font-mono-plex text-zinc-600">
            Scanning your text for grammar and spelling issues…
          </p>
        )}
        {state === 'error' && (
          <p data-testid="grammar-error" className="text-sm text-rose-800 font-mono-plex">
            Couldn't complete the grammar scan. Please try again later.
          </p>
        )}
        {clean && (
          <div data-testid="grammar-clean" className="flex items-center gap-2 text-emerald-800">
            <CheckCircle weight="fill" size={22} />
            <span className="text-sm font-semibold">No grammar or spelling issues detected.</span>
          </div>
        )}
        {showIssues && (
          <>
            <div className="flex items-center gap-3">
              <Warning weight="fill" size={22} className="text-amber-600" />
              <p className="text-base">
                <span data-testid="grammar-error-count" className="font-display font-bold text-3xl mr-2">
                  {count}
                </span>
                <span className="font-semibold">grammar / spelling issue{count === 1 ? '' : 's'} detected</span>
              </p>
            </div>
            <ul className="mt-4 space-y-2 max-h-40 overflow-auto pr-1">
              {(data.issues || []).slice(0, 5).map((iss, i) => (
                <li
                  key={i}
                  data-testid={`grammar-issue-${i}`}
                  className="text-sm border-l-4 border-black bg-zinc-50 px-3 py-2"
                >
                  <span className="font-mono-plex text-[10px] uppercase tracking-widest text-zinc-500 mr-2">
                    {iss.type}
                  </span>
                  <span>{iss.message}</span>
                  {iss.suggestion && (
                    <span className="ml-2 text-emerald-700 font-semibold">→ {iss.suggestion}</span>
                  )}
                </li>
              ))}
            </ul>

            <Link
              to="/tools/grammar-correct"
              data-testid="fix-grammar-link"
              className="brutal-btn secondary inline-flex items-center gap-2 mt-4"
            >
              Fix these automatically <ArrowRight weight="bold" size={16} />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
